import * as React from 'react';
import {ProvidedGlobalProps, withGlobalProps} from '../../../providers/globalPropsProvider';
import SlickSlider from 'react-slick';
import {IMediaItem, IVideoPlayer} from '../../../types/app-types';
import classNames from 'classnames';
import s from './ProductGalleryProvider.scss';
import {getMainImageRatio, isVideo} from '@wix/wixstores-client-core/dist/es/src/media/mediaService';
import _ from 'lodash';
import '../slick-carousel.global.scss';
import {useContext} from 'react';

export interface ProductGalleryProviderProps extends ProvidedGlobalProps {
  imageRatioId: number;
  media: IMediaItem[];
  withImageRatio: boolean;
}

interface ProductGalleryProviderState {
  selectedIndex: number;
  mainMediaSlider: SlickSlider;
  videoPlayers: Record<number, IVideoPlayer>;
  hasSelectedIndexChanged: boolean;
}

export interface IProductGalleryContext {
  selectedIndex: number;
  registerVideoPlayer(player: IVideoPlayer, index: number): void;
  changeSelectedIndex(selectedIndex: number): void;
  hasSelectedIndexChanged: boolean;
  setMainMediaSlider(mainMediaSlider: SlickSlider): void;
  mainMediaSlider: SlickSlider;
  imageRatioId: number;
}

export const ProductGalleryContext = React.createContext({
  selectedIndex: 0,
  registerVideoPlayer: _.noop,
  changeSelectedIndex: _.noop,
  hasSelectedIndexChanged: false,
  setMainMediaSlider: _.noop,
  mainMediaSlider: {} as SlickSlider,
  imageRatioId: -1,
} as IProductGalleryContext);

export const useProductGallery = () => useContext(ProductGalleryContext);

class ProductGalleryProviderComponent extends React.Component<
  ProductGalleryProviderProps,
  ProductGalleryProviderState
> {
  public state = {
    selectedIndex: 0,
    hasSelectedIndexChanged: false,
    mainMediaSlider: null,
    videoPlayers: {},
  };

  private readonly registerVideoPlayer = (player: IVideoPlayer, index: number) => {
    const {videoPlayers} = this.state;
    videoPlayers[index] = player;
    this.setState({videoPlayers});
  };

  private readonly changeSelectedIndex = (newlySelectedIndex: number) => {
    const {videoPlayers, mainMediaSlider, selectedIndex} = this.state;
    if (videoPlayers[selectedIndex]) {
      videoPlayers[selectedIndex].pause();
    }
    this.setState({selectedIndex: newlySelectedIndex});
    this.setState({hasSelectedIndexChanged: true});
    mainMediaSlider?.slickGoTo(newlySelectedIndex);
    if (videoPlayers[newlySelectedIndex]) {
      videoPlayers[newlySelectedIndex].play();
    }
  };

  private readonly setMainMediaSlider = (mainMediaRef: SlickSlider) => {
    const {mainMediaSlider} = this.state;
    if (mainMediaRef && mainMediaSlider !== mainMediaRef) {
      this.setState({mainMediaSlider: mainMediaRef});
    }
  };

  private readonly getImageRationClass = (media: IMediaItem, imageRatioId: number) => {
    const ratioClassSuffix = getMainImageRatio(media, imageRatioId).title.replace(':', '-');
    return `image-ratio-${ratioClassSuffix}`;
  };

  private readonly getComposite = () => {
    const {
      children,
      globals: {isMobile},
      imageRatioId,
      media,
      withImageRatio,
    } = this.props;
    const keyMedia = media ? media[0] : null;
    const imageRatio = this.getImageRationClass(keyMedia, imageRatioId);

    const getDynamicPaddingTop = () => {
      if (!withImageRatio || isMobile || !keyMedia || isVideo(keyMedia)) {
        return undefined;
      }

      const {width, height} = keyMedia;
      const ratio = (height / width) * 100;
      return `${ratio}%`;
    };

    const dynamicPaddingTop = getDynamicPaddingTop();
    const customPaddingTop = dynamicPaddingTop && imageRatioId === -1 && !isMobile;

    const productGalleryComposite = classNames(
      {[s.productGallery]: withImageRatio},
      {[s.withImageRatio]: withImageRatio && !isMobile},
      {[s[imageRatio]]: withImageRatio && !isMobile},
      {[s.productGallery]: !withImageRatio}
    );

    const responsivePaddingTop = customPaddingTop ? dynamicPaddingTop : undefined;
    const inlineStyles = {paddingTop: responsivePaddingTop};

    return (
      <div data-hook="product-gallery-composite" className={productGalleryComposite} style={inlineStyles}>
        {children}
      </div>
    );
  };

  public render(): JSX.Element {
    const {media, imageRatioId} = this.props;
    const {selectedIndex, hasSelectedIndexChanged, mainMediaSlider} = this.state;

    return media ? (
      <ProductGalleryContext.Provider
        value={{
          selectedIndex,
          mainMediaSlider,
          registerVideoPlayer: this.registerVideoPlayer,
          changeSelectedIndex: this.changeSelectedIndex,
          hasSelectedIndexChanged,
          setMainMediaSlider: this.setMainMediaSlider,
          imageRatioId,
        }}>
        {this.getComposite()}
      </ProductGalleryContext.Provider>
    ) : (
      this.getComposite()
    );
  }
}

export const ProductGalleryProvider = withGlobalProps(ProductGalleryProviderComponent);
